/**
 * A run, read and written: the office run page, the field run page and both
 * "add a run" pages draw these.
 *
 *   RunForm     — header clocks, kind, line, tool string, depths, pressure,
 *                 result, notes, and the kind's own section: survey stops
 *                 (bhp_survey), plugs and valves (set / pull / setting tool),
 *                 log intervals (logging), guns (perforating).
 *   RunReadout  — the same, read only, with the run's warnings.
 *
 * THE API DECIDES. Footage (2 × max depth), the clocks placed on the sheet,
 * the derived misfires (loaded − fired) and every warning are the API's; the
 * form sends what was typed. A child set is replaced only when its key is
 * sent, so `sources` is never sent here (sources are read only in WL-2) and a
 * section the kind does not show is not sent either.
 *
 * WARNINGS NEVER BLOCK. A misfire count that disagrees with loaded − fired,
 * and a first run with no rig-up test (setting off), save and come back as
 * warnings. The one refusal is the API's 409 rig_up_test_required when the
 * tenant's setting requires the test — shown as a refusal, not a warning.
 */
import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { fmtWlNumber, wlToInput, WL_TIME_PATTERN } from '../../lib/wireline-slickline';
import { WL_RUN_KIND_SECTIONS, wlClockRange, wlQty, wlWarningText } from '../../lib/wireline-slickline-jobs';
import type { WlCatalog, WlMeta } from '../../types/wireline-slickline';
import type { WlRun, WlRunWrite, WlWarningEntry } from '../../types/wireline-slickline-jobs';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WL_SMALL_BTN, WlEmpty, WlField, WlFormError, WlPanel, WlSpec, WlWarning } from './WlBits';

type Section = 'stops' | 'plugs' | 'logs' | 'guns';

const FALLBACK_KINDS = [
  'gauge_drift', 'bhp_survey', 'fishing', 'plug_set', 'plug_pull', 'valve_set', 'valve_pull', 'bailing', 'logging', 'perforating', 'setting_tool', 'other',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));
const FALLBACK_RESULTS = [
  { value: 'success', label: 'Success' },
  { value: 'partial', label: 'Partial' },
  { value: 'failed', label: 'Failed' },
  { value: 'aborted', label: 'Aborted' },
];

/** The sections a run shows: its kind's, plus any that already hold rows. */
export function runSections(kind: string, run?: WlRun | null): Section[] {
  const out = new Set<Section>(WL_RUN_KIND_SECTIONS[kind] ?? []);
  if (run?.surveyStops?.length) out.add('stops');
  if (run?.plugs?.length) out.add('plugs');
  if (run?.logIntervals?.length) out.add('logs');
  if (run?.guns?.length) out.add('guns');
  return (['stops', 'plugs', 'logs', 'guns'] as Section[]).filter((s) => out.has(s));
}

type Row = Record<string, string | boolean>;
const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const numOrNull = (v: string | boolean | undefined) => (typeof v !== 'string' || v.trim() === '' ? null : Number(v));
const textOrNull = (v: string | boolean | undefined) => (typeof v !== 'string' || v.trim() === '' ? null : v.trim());
const intOrNull = numOrNull;

export interface RunOption {
  id: string;
  name: string;
}

export function RunForm({
  run,
  catalog,
  meta,
  lines,
  toolStrings,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  run: WlRun | null;
  catalog: WlCatalog | null;
  meta: WlMeta | null;
  lines: RunOption[];
  toolStrings: RunOption[];
  pending: boolean;
  /** A refusal (400 / 409 / 403) as a sentence. */
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: WlRunWrite) => void;
}) {
  const depthUnit = meta?.units?.depth ?? 'm';
  const pressureUnits = catalog?.pressureUnits?.length ? catalog.pressureUnits : ['kPa', 'psi'];
  const tempUnits = catalog?.temperatureUnits?.length ? catalog.temperatureUnits : ['C', 'F'];
  const densityUnits = catalog?.shotDensityUnits?.length ? catalog.shotDensityUnits : ['spm', 'spf'];
  const lengthUnits = catalog?.lengthUnits?.length ? catalog.lengthUnits : ['m', 'ft'];
  const defaultPressure = meta?.unitSystem === 'imperial' ? 'psi' : pressureUnits[0];
  const defaultTemp = meta?.unitSystem === 'imperial' ? tempUnits.find((u) => /f/i.test(u)) ?? tempUnits[0] : tempUnits[0];
  const defaultDensity = meta?.unitSystem === 'imperial' ? densityUnits.find((u) => /f/i.test(u)) ?? densityUnits[0] : densityUnits[0];

  const [kind, setKind] = useState(run?.runKind ?? '');
  const [h, setH] = useState({
    rigUp: run?.rigUpStart ?? '',
    rih: run?.rih ?? '',
    onDepth: run?.onDepth ?? '',
    pooh: run?.pooh ?? '',
    rigDown: run?.rigDown ?? '',
    lineId: run?.lineId ?? '',
    toolStringId: run?.toolStringId ?? '',
    maxDepth: wlToInput(run?.maxDepth?.value),
    maxDepthUnit: run?.maxDepth?.unit ?? depthUnit,
    tagged: wlToInput(run?.taggedDepth?.value),
    taggedUnit: run?.taggedDepth?.unit ?? depthUnit,
    pressure: wlToInput(run?.surfacePressure?.value),
    pressureUnit: run?.surfacePressure?.unit ?? defaultPressure,
    result: run?.result ?? '',
    notes: run?.notes ?? '',
  });
  const [stops, setStops] = useState<Row[]>(
    (run?.surveyStops ?? []).map((x) => ({
      depth: s(x.depth?.value), depthUnit: x.depth?.unit ?? depthUnit, pressure: s(x.pressure?.value), pressureUnit: x.pressure?.unit ?? defaultPressure,
      temperature: s(x.temperature?.value), temperatureUnit: x.temperature?.unit ?? defaultTemp, dwell: s(x.dwellMinutes), notes: s(x.notes),
    })),
  );
  const [plugs, setPlugs] = useState<Row[]>(
    (run?.plugs ?? []).map((x) => ({
      action: x.action, plugKind: x.plugKind, size: s(x.sizeText), serial: s(x.serialNumber), owner: x.owner ?? 'ours',
      depth: s(x.depth?.value), depthUnit: x.depth?.unit ?? depthUnit, notes: s(x.notes), equipmentId: s(x.equipmentId),
    })),
  );
  const [logs, setLogs] = useState<Row[]>(
    (run?.logIntervals ?? []).map((x) => ({
      logType: x.logType, top: s(x.top?.value), bottom: s(x.bottom?.value), depthUnit: x.top?.unit ?? depthUnit, repeat: !!x.repeatPass, notes: s(x.notes),
    })),
  );
  const [guns, setGuns] = useState<Row[]>(
    (run?.guns ?? []).map((x) => ({
      position: s(x.gunPosition), system: s(x.gunSystem), size: s(x.gunSize), chargeType: s(x.chargeType), explosive: s(x.explosiveType),
      loaded: s(x.shotsLoaded), fired: s(x.shotsFired), misfires: s(x.misfireCount), disposition: s(x.misfireDisposition),
      density: s(x.shotDensity?.value), densityUnit: x.shotDensity?.unit ?? defaultDensity, phasing: s(x.phasingDeg),
      top: s(x.top?.value), bottom: s(x.bottom?.value), depthUnit: x.top?.unit ?? depthUnit, lot: s(x.explosiveLotRef), notes: s(x.notes),
      // Carried, not edited here: the API replaces every gun when `guns` is sent, so a
      // field this form has no input for must still go back or it is erased.
      maker: s(x.chargeMaker), mass: s(x.chargeMassG), detonator: s(x.detonatorKind),
    })),
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const sections = kind ? runSections(kind, run) : [];
  const kinds = catalog?.runKinds?.length ? catalog.runKinds : FALLBACK_KINDS;
  const results = catalog?.runResults?.length ? catalog.runResults : FALLBACK_RESULTS;
  const plugActions = catalog?.plugActions ?? [];
  const plugKinds = catalog?.plugKinds ?? [];
  const owners = catalog?.toolOwners?.length ? catalog.toolOwners : [{ value: 'ours', label: 'Ours' }, { value: 'operator', label: 'Operator' }, { value: 'rented', label: 'Rented' }];
  const setHF = (k: keyof typeof h) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setH((x) => ({ ...x, [k]: e.target.value }));

  const submit = () => {
    setLocalError(null);
    if (!kind) return setLocalError('Choose what kind of run this was.');
    if (!h.rigUp || !h.rigDown) return setLocalError('Rig-up start and rig down are needed.');
    for (const t of [h.rigUp, h.rih, h.onDepth, h.pooh, h.rigDown]) if (t && !WL_TIME_PATTERN.test(t)) return setLocalError('Times are 24-hour HH:MM, e.g. 07:30.');
    const body: WlRunWrite = {
      run_kind: kind,
      rig_up_start: h.rigUp,
      rih: h.rih || null,
      on_depth: h.onDepth || null,
      pooh: h.pooh || null,
      rig_down: h.rigDown,
      line_id: h.lineId || null,
      tool_string_id: h.toolStringId || null,
      max_depth: numOrNull(h.maxDepth),
      tagged_depth: numOrNull(h.tagged),
      surface_pressure: numOrNull(h.pressure),
      result: h.result || null,
      notes: textOrNull(h.notes),
    };
    if (body.max_depth !== null) body.max_depth_unit = h.maxDepthUnit;
    if (body.tagged_depth !== null) body.tagged_depth_unit = h.taggedUnit;
    if (body.surface_pressure !== null) body.surface_pressure_unit = h.pressureUnit;
    if (sections.includes('stops')) {
      body.survey_stops = stops.map((r) => ({
        depth: numOrNull(r.depth), depth_unit: r.depthUnit,
        ...(numOrNull(r.pressure) !== null ? { pressure: numOrNull(r.pressure), pressure_unit: r.pressureUnit } : {}),
        ...(numOrNull(r.temperature) !== null ? { temperature: numOrNull(r.temperature), temperature_unit: r.temperatureUnit } : {}),
        dwell_minutes: intOrNull(r.dwell), notes: textOrNull(r.notes),
      }));
    }
    if (sections.includes('plugs')) {
      body.plugs = plugs.map((r) => ({
        action: r.action, plug_kind: r.plugKind, size_text: textOrNull(r.size), serial_number: textOrNull(r.serial), owner: r.owner || 'ours',
        ...(r.equipmentId ? { equipment_id: r.equipmentId } : {}),
        ...(numOrNull(r.depth) !== null ? { depth: numOrNull(r.depth), depth_unit: r.depthUnit } : {}),
        notes: textOrNull(r.notes),
      }));
    }
    if (sections.includes('logs')) {
      body.log_intervals = logs.map((r) => ({
        log_type: textOrNull(r.logType), top: numOrNull(r.top), bottom: numOrNull(r.bottom), depth_unit: r.depthUnit, repeat_pass: !!r.repeat, notes: textOrNull(r.notes),
      }));
    }
    if (sections.includes('guns')) {
      body.guns = guns.map((r, i) => ({
        gun_position: intOrNull(r.position) ?? i + 1,
        gun_system: textOrNull(r.system), gun_size: textOrNull(r.size), charge_type: textOrNull(r.chargeType), explosive_type: textOrNull(r.explosive),
        shots_loaded: intOrNull(r.loaded), shots_fired: intOrNull(r.fired), misfire_count: intOrNull(r.misfires), misfire_disposition: textOrNull(r.disposition),
        ...(numOrNull(r.density) !== null ? { shot_density: numOrNull(r.density), shot_density_unit: r.densityUnit } : {}),
        phasing_deg: numOrNull(r.phasing),
        ...(numOrNull(r.top) !== null || numOrNull(r.bottom) !== null ? { top: numOrNull(r.top), bottom: numOrNull(r.bottom), depth_unit: r.depthUnit } : {}),
        explosive_lot_ref: textOrNull(r.lot), notes: textOrNull(r.notes),
        charge_maker: textOrNull(r.maker), charge_mass_g: numOrNull(r.mass), detonator_kind: textOrNull(r.detonator),
      }));
    }
    onSubmit(body);
  };

  const unitSelect = (label: string, value: string, units: string[], onChange: (v: string) => void) => (
    <select className={WL_INPUT} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {units.map((u) => (
        <option key={u} value={u}>
          {u}
        </option>
      ))}
    </select>
  );
  const cell = (rows: Row[], set: React.Dispatch<React.SetStateAction<Row[]>>, i: number, key: string, label: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <input
      className={WL_INPUT}
      aria-label={label}
      placeholder={extra.placeholder ?? label.replace(/^[A-Za-z]+ \d+ /, '')}
      value={String(rows[i][key] ?? '')}
      onChange={(e) => set((all) => all.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)))}
      {...extra}
    />
  );
  const removeBtn = (set: React.Dispatch<React.SetStateAction<Row[]>>, i: number, label: string) => (
    <button type="button" className={WL_SMALL_BTN} aria-label={label} onClick={() => set((all) => all.filter((_, j) => j !== i))}>
      <Trash2 className="size-3.5" aria-hidden="true" />
    </button>
  );
  const addBtn = (label: string, onClick: () => void, testId: string) => (
    <button type="button" className={WL_SMALL_BTN} onClick={onClick} data-testid={testId}>
      <Plus className="size-3.5" aria-hidden="true" />
      {label}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="wl-run-form">
      <WlPanel eyebrow="Run" title="Run">
        <div className="space-y-3 px-4 py-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <WlField label="Kind">
              <select className={WL_INPUT} aria-label="Run kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">Choose a kind</option>
                {kinds.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </WlField>
            <WlField label="Line">
              <select className={WL_INPUT} aria-label="Line" value={h.lineId} onChange={setHF('lineId')}>
                <option value="">—</option>
                {lines.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </WlField>
            <WlField label="Tool string" hint="Its items are copied onto the run">
              <select className={WL_INPUT} aria-label="Tool string" value={h.toolStringId} onChange={setHF('toolStringId')}>
                <option value="">—</option>
                {toolStrings.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </WlField>
            <WlField label="Result">
              <select className={WL_INPUT} aria-label="Result" value={h.result} onChange={setHF('result')}>
                <option value="">—</option>
                {results.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </WlField>
          </div>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-5">
            <WlField label="Rig-up start">
              <input className={WL_INPUT} aria-label="Rig-up start" placeholder="HH:MM" value={h.rigUp} onChange={setHF('rigUp')} />
            </WlField>
            <WlField label="RIH">
              <input className={WL_INPUT} aria-label="RIH" placeholder="HH:MM" value={h.rih} onChange={setHF('rih')} />
            </WlField>
            <WlField label="On depth">
              <input className={WL_INPUT} aria-label="On depth" placeholder="HH:MM" value={h.onDepth} onChange={setHF('onDepth')} />
            </WlField>
            <WlField label="POOH">
              <input className={WL_INPUT} aria-label="POOH" placeholder="HH:MM" value={h.pooh} onChange={setHF('pooh')} />
            </WlField>
            <WlField label="Rig down">
              <input className={WL_INPUT} aria-label="Rig down" placeholder="HH:MM" value={h.rigDown} onChange={setHF('rigDown')} />
            </WlField>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <WlField label="Max depth" hint="Footage is worked out by the server">
              <div className="flex gap-2">
                <input inputMode="decimal" className={WL_INPUT} aria-label="Max depth" value={h.maxDepth} onChange={setHF('maxDepth')} />
                {unitSelect('Max depth unit', h.maxDepthUnit, lengthUnits, (v) => setH((x) => ({ ...x, maxDepthUnit: v })))}
              </div>
            </WlField>
            <WlField label="Tagged depth">
              <div className="flex gap-2">
                <input inputMode="decimal" className={WL_INPUT} aria-label="Tagged depth" value={h.tagged} onChange={setHF('tagged')} />
                {unitSelect('Tagged depth unit', h.taggedUnit, lengthUnits, (v) => setH((x) => ({ ...x, taggedUnit: v })))}
              </div>
            </WlField>
            <WlField label="Surface pressure">
              <div className="flex gap-2">
                <input inputMode="decimal" className={WL_INPUT} aria-label="Surface pressure" value={h.pressure} onChange={setHF('pressure')} />
                {unitSelect('Surface pressure unit', h.pressureUnit, pressureUnits, (v) => setH((x) => ({ ...x, pressureUnit: v })))}
              </div>
            </WlField>
          </div>
          <WlField label="Notes">
            <textarea className={`${WL_INPUT} h-20 py-2`} aria-label="Run notes" value={h.notes} onChange={setHF('notes')} />
          </WlField>
        </div>
      </WlPanel>

      {sections.includes('stops') && (
        <WlPanel
          eyebrow="Survey"
          title="Survey stops"
          testId="wl-run-stops-edit"
          actions={addBtn('Add stop', () => setStops((r) => [...r, { depth: '', depthUnit, pressure: '', pressureUnit: defaultPressure, temperature: '', temperatureUnit: defaultTemp, dwell: '', notes: '' }]), 'wl-add-stop')}
        >
          {stops.length === 0 ? (
            <WlEmpty>No stop yet.</WlEmpty>
          ) : (
            <ul className="divide-y divide-fieldiq-border">
              {stops.map((r, i) => (
                <li key={i} className="grid gap-2 px-4 py-3 grid-cols-2 lg:grid-cols-[1fr_0.6fr_1fr_0.6fr_1fr_0.6fr_0.8fr_auto]">
                  {cell(stops, setStops, i, 'depth', `Stop ${i + 1} depth`, { inputMode: 'decimal' })}
                  {unitSelect(`Stop ${i + 1} depth unit`, String(r.depthUnit), lengthUnits, (v) => setStops((a) => a.map((x, j) => (j === i ? { ...x, depthUnit: v } : x))))}
                  {cell(stops, setStops, i, 'pressure', `Stop ${i + 1} pressure`, { inputMode: 'decimal' })}
                  {unitSelect(`Stop ${i + 1} pressure unit`, String(r.pressureUnit), pressureUnits, (v) => setStops((a) => a.map((x, j) => (j === i ? { ...x, pressureUnit: v } : x))))}
                  {cell(stops, setStops, i, 'temperature', `Stop ${i + 1} temperature`, { inputMode: 'decimal' })}
                  {unitSelect(`Stop ${i + 1} temperature unit`, String(r.temperatureUnit), tempUnits, (v) => setStops((a) => a.map((x, j) => (j === i ? { ...x, temperatureUnit: v } : x))))}
                  {cell(stops, setStops, i, 'dwell', `Stop ${i + 1} dwell minutes`, { inputMode: 'numeric', placeholder: 'Dwell min' })}
                  {removeBtn(setStops, i, `Remove stop ${i + 1}`)}
                </li>
              ))}
            </ul>
          )}
        </WlPanel>
      )}

      {sections.includes('plugs') && (
        <WlPanel
          eyebrow="Plugs and valves"
          title="Plugs and valves"
          testId="wl-run-plugs-edit"
          actions={addBtn(
            'Add plug or valve',
            () =>
              setPlugs((r) => [
                ...r,
                { action: plugActions[0]?.value ?? '', plugKind: plugKinds[0]?.value ?? '', size: '', serial: '', owner: 'ours', depth: '', depthUnit, notes: '', equipmentId: '' },
              ]),
            'wl-add-plug',
          )}
        >
          {plugs.length === 0 ? (
            <WlEmpty>No plug or valve yet.</WlEmpty>
          ) : (
            <ul className="divide-y divide-fieldiq-border">
              {plugs.map((r, i) => (
                <li key={i} className="grid gap-2 px-4 py-3 grid-cols-2 lg:grid-cols-[1fr_1fr_0.8fr_0.8fr_0.8fr_0.6fr_0.8fr_auto]">
                  <select className={WL_INPUT} aria-label={`Plug ${i + 1} action`} value={String(r.action)} onChange={(e) => setPlugs((a) => a.map((x, j) => (j === i ? { ...x, action: e.target.value } : x)))}>
                    {plugActions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <select className={WL_INPUT} aria-label={`Plug ${i + 1} kind`} value={String(r.plugKind)} onChange={(e) => setPlugs((a) => a.map((x, j) => (j === i ? { ...x, plugKind: e.target.value } : x)))}>
                    {plugKinds.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {cell(plugs, setPlugs, i, 'size', `Plug ${i + 1} size`)}
                  {cell(plugs, setPlugs, i, 'serial', `Plug ${i + 1} serial`)}
                  {cell(plugs, setPlugs, i, 'depth', `Plug ${i + 1} depth`, { inputMode: 'decimal' })}
                  {unitSelect(`Plug ${i + 1} depth unit`, String(r.depthUnit), lengthUnits, (v) => setPlugs((a) => a.map((x, j) => (j === i ? { ...x, depthUnit: v } : x))))}
                  <select className={WL_INPUT} aria-label={`Plug ${i + 1} owner`} value={String(r.owner)} onChange={(e) => setPlugs((a) => a.map((x, j) => (j === i ? { ...x, owner: e.target.value } : x)))}>
                    {owners.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {removeBtn(setPlugs, i, `Remove plug ${i + 1}`)}
                </li>
              ))}
            </ul>
          )}
        </WlPanel>
      )}

      {sections.includes('logs') && (
        <WlPanel
          eyebrow="Logging"
          title="Log intervals"
          testId="wl-run-logs-edit"
          actions={addBtn('Add interval', () => setLogs((r) => [...r, { logType: '', top: '', bottom: '', depthUnit, repeat: false, notes: '' }]), 'wl-add-log')}
        >
          {logs.length === 0 ? (
            <WlEmpty>No interval yet.</WlEmpty>
          ) : (
            <ul className="divide-y divide-fieldiq-border">
              {logs.map((r, i) => (
                <li key={i} className="grid gap-2 px-4 py-3 grid-cols-2 lg:grid-cols-[1.4fr_0.8fr_0.8fr_0.6fr_auto_auto]">
                  {cell(logs, setLogs, i, 'logType', `Interval ${i + 1} log type`, { placeholder: 'Log type' })}
                  {cell(logs, setLogs, i, 'top', `Interval ${i + 1} top`, { inputMode: 'decimal' })}
                  {cell(logs, setLogs, i, 'bottom', `Interval ${i + 1} bottom`, { inputMode: 'decimal' })}
                  {unitSelect(`Interval ${i + 1} depth unit`, String(r.depthUnit), lengthUnits, (v) => setLogs((a) => a.map((x, j) => (j === i ? { ...x, depthUnit: v } : x))))}
                  <label className="inline-flex items-center gap-2 text-sm text-fieldiq-text">
                    <input type="checkbox" checked={!!r.repeat} onChange={(e) => setLogs((a) => a.map((x, j) => (j === i ? { ...x, repeat: e.target.checked } : x)))} />
                    Repeat pass
                  </label>
                  {removeBtn(setLogs, i, `Remove interval ${i + 1}`)}
                </li>
              ))}
            </ul>
          )}
        </WlPanel>
      )}

      {sections.includes('guns') && (
        <WlPanel
          eyebrow="Perforating"
          title="Guns"
          testId="wl-run-guns-edit"
          actions={addBtn(
            'Add gun',
            () =>
              setGuns((r) => [
                ...r,
                {
                  position: String(r.length + 1), system: '', size: '', chargeType: '', explosive: '', loaded: '', fired: '', misfires: '', disposition: '',
                  density: '', densityUnit: defaultDensity, phasing: '', top: '', bottom: '', depthUnit, lot: '', notes: '',
                },
              ]),
            'wl-add-gun',
          )}
        >
          <p className="px-4 pt-3 text-xs text-fieldiq-muted">Misfires entered are checked against loaded minus fired; a mismatch is a warning, never a refusal.</p>
          {guns.length === 0 ? (
            <WlEmpty>No gun yet.</WlEmpty>
          ) : (
            <ul className="divide-y divide-fieldiq-border">
              {guns.map((r, i) => (
                <li key={i} className="grid gap-2 px-4 py-3 grid-cols-2 sm:grid-cols-4 lg:grid-cols-8" data-testid="wl-gun-edit-row">
                  {cell(guns, setGuns, i, 'position', `Gun ${i + 1} position`, { inputMode: 'numeric', placeholder: 'Position' })}
                  {cell(guns, setGuns, i, 'loaded', `Gun ${i + 1} shots loaded`, { inputMode: 'numeric', placeholder: 'Loaded' })}
                  {cell(guns, setGuns, i, 'fired', `Gun ${i + 1} shots fired`, { inputMode: 'numeric', placeholder: 'Fired' })}
                  {cell(guns, setGuns, i, 'misfires', `Gun ${i + 1} misfires`, { inputMode: 'numeric', placeholder: 'Misfires' })}
                  {cell(guns, setGuns, i, 'top', `Gun ${i + 1} top`, { inputMode: 'decimal', placeholder: 'Top' })}
                  {cell(guns, setGuns, i, 'bottom', `Gun ${i + 1} bottom`, { inputMode: 'decimal', placeholder: 'Bottom' })}
                  {cell(guns, setGuns, i, 'chargeType', `Gun ${i + 1} charge`, { placeholder: 'Charge' })}
                  <div className="flex gap-2">
                    {cell(guns, setGuns, i, 'lot', `Gun ${i + 1} lot`, { placeholder: 'Lot' })}
                    {removeBtn(setGuns, i, `Remove gun ${i + 1}`)}
                  </div>
                  {cell(guns, setGuns, i, 'disposition', `Gun ${i + 1} misfire disposition`, { placeholder: 'Misfire disposition', className: `${WL_INPUT} col-span-2 sm:col-span-4 lg:col-span-8` })}
                </li>
              ))}
            </ul>
          )}
        </WlPanel>
      )}

      <WlFormError message={localError ?? error} />
      <div className="flex justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="button" className={WL_PRIMARY} onClick={submit} disabled={pending} data-testid="wl-run-save">
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

/** The run's warnings, plus a write's own (the create / save response). */
export function RunWarnings({ warnings, testId = 'wl-run-warnings' }: { warnings: WlWarningEntry[]; testId?: string }) {
  if (!warnings.length) return null;
  return (
    <div className="space-y-2" data-testid={testId}>
      {warnings.map((w, i) => (
        <WlWarning key={`${w.code}-${i}`} message={wlWarningText(w)} testId={`wl-warning-${w.code}`} />
      ))}
    </div>
  );
}

const misfireFlag = (run: WlRun, position: number) => run.warnings.some((w) => w.code === 'misfire_count_mismatch' && w.gunPosition === position);

export function RunReadout({ run }: { run: WlRun }) {
  return (
    <>
      <RunWarnings warnings={run.warnings} />
      <WlPanel eyebrow="Run" title={`Run ${run.runNumber} · ${run.runKindLabel ?? run.runKind}`} testId="wl-run-readout">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <WlSpec label="Rig-up – rig down" value={wlClockRange(run.rigUpStart, run.rigDown)} mono />
          <WlSpec label="RIH / on depth / POOH" value={`${run.rih ?? '—'} / ${run.onDepth ?? '—'} / ${run.pooh ?? '—'}`} mono />
          <WlSpec label="Result" value={run.resultLabel ?? '—'} />
          <WlSpec label="Line" value={run.lineNumber || '—'} mono />
          <WlSpec label="Max depth" value={wlQty(run.maxDepth, 2)} mono />
          <WlSpec label="Tagged depth" value={wlQty(run.taggedDepth, 2)} mono />
          <WlSpec label="Surface pressure" value={wlQty(run.surfacePressure, 2)} mono />
          <WlSpec label="Footage" value={wlQty(run.footage, 1)} mono />
        </dl>
        {run.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{run.notes}</p>}
      </WlPanel>

      {run.surveyStops.length > 0 && (
        <WlPanel eyebrow="Survey" title="Survey stops" testId="wl-run-stops">
          <Table
            head={['Depth', 'Pressure', 'Temperature', 'Dwell']}
            rows={run.surveyStops.map((x) => [wlQty(x.depth, 2), wlQty(x.pressure, 2), wlQty(x.temperature, 1), x.dwellMinutes === null ? '—' : `${fmtWlNumber(x.dwellMinutes)} min`])}
          />
        </WlPanel>
      )}
      {run.plugs.length > 0 && (
        <WlPanel eyebrow="Plugs and valves" title="Plugs and valves" testId="wl-run-plugs">
          <Table
            head={['Action', 'Kind', 'Size', 'Serial', 'Depth', 'Owner']}
            rows={run.plugs.map((x) => [x.actionLabel ?? x.action, x.plugKindLabel ?? x.plugKind, x.sizeText || '—', x.serialNumber || '—', wlQty(x.depth, 2), x.owner || '—'])}
          />
        </WlPanel>
      )}
      {run.logIntervals.length > 0 && (
        <WlPanel eyebrow="Logging" title="Log intervals" testId="wl-run-logs">
          <Table
            head={['Log', 'Top', 'Bottom', 'Repeat']}
            rows={run.logIntervals.map((x) => [x.logType, wlQty(x.top, 2), wlQty(x.bottom, 2), x.repeatPass ? 'Yes' : 'No'])}
          />
        </WlPanel>
      )}
      {run.guns.length > 0 && (
        <WlPanel eyebrow="Perforating" title="Guns" testId="wl-run-guns">
          <Table
            head={['Gun', 'Loaded', 'Fired', 'Loaded − fired', 'Misfires entered', 'Interval', 'Charge', 'Lot']}
            rowTestId="wl-gun-row"
            flag={run.guns.map((g) => misfireFlag(run, g.gunPosition))}
            rows={run.guns.map((g) => [
              String(g.gunPosition),
              fmtWlNumber(g.shotsLoaded),
              fmtWlNumber(g.shotsFired),
              g.derivedMisfires === null ? '—' : fmtWlNumber(g.derivedMisfires),
              g.misfireCount === null ? '—' : fmtWlNumber(g.misfireCount),
              g.top && g.bottom ? `${wlQty(g.top, 2)} – ${wlQty(g.bottom, 2)}` : '—',
              g.chargeType || '—',
              g.explosiveLotRef || '—',
            ])}
          />
        </WlPanel>
      )}
      {run.sources.length > 0 && (
        <WlPanel eyebrow="Radiation" title="Sources" testId="wl-run-sources">
          <Table head={['Isotope', 'Serial', 'Notes']} rows={run.sources.map((x) => [x.isotope || '—', x.serialNumber || '—', x.notes || '—'])} />
        </WlPanel>
      )}
    </>
  );
}

function Table({ head, rows, flag, rowTestId }: { head: string[]; rows: string[][]; flag?: boolean[]; rowTestId?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="text-left text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            {head.map((h) => (
              <th key={h} className="px-4 py-2">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-fieldiq-border">
          {rows.map((r, i) => (
            <tr key={i} className={flag?.[i] ? 'bg-fi-signal-amber/10' : ''} data-testid={rowTestId} data-flagged={flag?.[i] ? 'true' : undefined}>
              {r.map((c, j) => (
                <td key={j} className="px-4 py-2 font-mono text-fieldiq-text">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
